import React, { useState, useEffect } from 'react';
import { Users, UserPlus, UserCheck, Search, MessageCircle, UserMinus, X, Check, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Friends({ user, userRole, onStartMessage }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Helper function to format Firebase timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    // Handle Firebase Timestamp object
    if (timestamp._seconds !== undefined) {
      const date = new Date(timestamp._seconds * 1000);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

      return date.toLocaleDateString();
    }

    return timestamp;
  };

  // Load real data from API
  useEffect(() => {
    if (user) {
      loadAllData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadAllData = async () => {
    await Promise.all([
      loadFriends(),
      loadFriendRequests(),
      loadSentRequests(),
      loadFollowers(),
      loadFollowing()
    ]);
    setLoading(false);
  };

  const loadFriends = async () => {
    try {
      const response = await fetch(`${API_URL}/api/friends/${user.uid}`);
      const data = await response.json();
      if (data.success) {
        setFriends(data.friends || []);
      }
    } catch (error) {
      console.error('Error loading friends:', error);
    }
  };

  const loadFriendRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/friends/requests/${user.uid}`);
      const data = await response.json();
      if (data.success) {
        setFriendRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error loading friend requests:', error);
    }
  };

  const loadSentRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/friends/requests/sent/${user.uid}`);
      const data = await response.json();
      if (data.success) {
        setSentRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error loading sent requests:', error);
    }
  };

  const loadFollowers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/${user.uid}/followers`);
      const data = await response.json();
      if (data.success) {
        // Convert followers to the expected format
        const followersData = data.followers.map(follower => ({
          id: follower.userId,
          name: follower.displayName,
          avatar: follower.avatar,
          username: follower.username || `@user${follower.userId.substring(0, 6)}`,
          isFollowingBack: false // Will be updated below
        }));

        // Check if user is following back
        const followingResponse = await fetch(`${API_URL}/api/users/${user.uid}/following`);
        const followingData = await followingResponse.json();
        if (followingData.success) {
          const followingIds = followingData.following.map(f => f.userId);
          followersData.forEach(follower => {
            follower.isFollowingBack = followingIds.includes(follower.id);
          });
        }

        setFollowers(followersData);
      }
    } catch (error) {
      console.error('Error loading followers:', error);
    }
  };

  const loadFollowing = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/${user.uid}/following`);
      const data = await response.json();
      if (data.success) {
        // Convert following to the expected format
        const followingData = data.following.map(person => ({
          id: person.userId,
          name: person.displayName,
          avatar: person.avatar,
          username: person.username || `@user${person.userId.substring(0, 6)}`,
          isFollower: false // Will be updated below
        }));

        // Check who follows back
        const followersResponse = await fetch(`${API_URL}/api/users/${user.uid}/followers`);
        const followersData = await followersResponse.json();
        if (followersData.success) {
          const followerIds = followersData.followers.map(f => f.userId);
          followingData.forEach(person => {
            person.isFollower = followerIds.includes(person.id);
          });
        }

        setFollowing(followingData);
      }
    } catch (error) {
      console.error('Error loading following:', error);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/api/friends/request/${requestId}/accept`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        await loadFriends();
        await loadFriendRequests();
        await loadSentRequests();
      }
    } catch (error) {
      console.error('Error accepting request:', error);
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/api/friends/request/${requestId}/reject`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        await loadFriendRequests();
        await loadSentRequests();
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  const handleCancelSentRequest = async (toUserId) => {
    try {
      const response = await fetch(`${API_URL}/api/friends/request/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromUserId: user.uid, toUserId })
      });
      const data = await response.json();
      if (data.success) {
        await loadSentRequests();
      }
    } catch (error) {
      console.error('Error canceling request:', error);
    }
  };

  const handleUnfriend = async (friendId) => {
    if (!window.confirm('Are you sure you want to unfriend this person?')) return;

    try {
      const response = await fetch(`${API_URL}/api/friends/unfriend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, friendId })
      });
      const data = await response.json();
      if (data.success) {
        await loadFriends();
      }
    } catch (error) {
      console.error('Error unfriending:', error);
    }
  };

  const handleFollowBack = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/follow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ followerId: user.uid })
      });
      const data = await response.json();
      if (data.success) {
        await loadFollowers();
        await loadFollowing();
      }
    } catch (error) {
      console.error('Error following back:', error);
    }
  };

  const handleUnfollow = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/follow`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ followerId: user.uid })
      });
      const data = await response.json();
      if (data.success) {
        await loadFollowing();
      }
    } catch (error) {
      console.error('Error unfollowing:', error);
    }
  };

  const handleRemoveFollower = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this follower?')) return;

    // This would require a new API endpoint to remove a follower
    // For now, we can just unfollow them if we're following back
    console.log('Remove follower functionality needs backend implementation');
  };

  // Filter based on search query
  const getFilteredData = () => {
    let data = [];
    switch (activeTab) {
      case 'friends':
        data = friends;
        break;
      case 'requests':
        data = friendRequests;
        break;
      case 'sent':
        data = sentRequests;
        break;
      case 'followers':
        data = followers;
        break;
      case 'following':
        data = following;
        break;
      default:
        data = [];
    }

    return data.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.username.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const filteredData = getFilteredData();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-8 h-8" />
            {t('friends.friendsAndConnections')}
          </h1>
          <p className="text-gray-600 mt-2">
            {t('friends.connectWithTraders')}
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 bg-white rounded-lg shadow-sm p-2">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('friends')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'friends'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Users className="w-4 h-4" />
              {t('friends.friends')} ({friends.length})
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'requests'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <UserPlus className="w-4 h-4" />
              {t('friends.received')} ({friendRequests.length})
            </button>
            <button
              onClick={() => setActiveTab('sent')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'sent'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <UserPlus className="w-4 h-4" />
              {t('friends.sent')} ({sentRequests.length})
            </button>
            <button
              onClick={() => setActiveTab('followers')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'followers'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <UserCheck className="w-4 h-4" />
              {t('friends.followers')} ({followers.length})
            </button>
            <button
              onClick={() => setActiveTab('following')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'following'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <UserMinus className="w-4 h-4" />
              {t('friends.following')} ({following.length})
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={`${t('friends.searchPlaceholder')} ${activeTab}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Friends List */}
        {activeTab === 'friends' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('friends.yourFriends')} ({filteredData.length})
              </h2>
            </div>

            {filteredData.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>{t('friends.noFriendsFound')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredData.map((friend) => (
                  <div
                    key={friend.id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                            <span className="text-2xl">{friend.avatar}</span>
                          </div>
                          {friend.status === 'online' && (
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                          )}
                        </div>

                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {friend.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {friend.username} • {friend.mutualFriends} {t('friends.mutualFriends')}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => onStartMessage && onStartMessage(friend.id)}
                          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <MessageCircle className="w-4 h-4" />
                          {t('friends.message')}
                        </button>
                        <button
                          onClick={() => handleUnfriend(friend.id)}
                          className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <UserX className="w-4 h-4" />
                          {t('friends.unfriend')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Friend Requests */}
        {activeTab === 'requests' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('friends.friendRequests')} ({filteredData.length})
              </h2>
            </div>

            {filteredData.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                <UserPlus className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>{t('friends.noPendingRequests')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredData.map((request) => (
                  <div
                    key={request.id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                          <span className="text-2xl">{request.avatar}</span>
                        </div>

                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {request.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {request.username} • {request.mutualFriends} {t('friends.mutualFriends')}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimestamp(request.requestedAt)}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAcceptRequest(request.id)}
                          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          {t('friends.accept')}
                        </button>
                        <button
                          onClick={() => handleRejectRequest(request.id)}
                          className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          {t('friends.reject')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sent Requests */}
        {activeTab === 'sent' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('friends.sentFriendRequests')} ({filteredData.length})
              </h2>
            </div>

            {filteredData.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                <UserPlus className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>{t('friends.noPendingSentRequests')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredData.map((request) => (
                  <div
                    key={request.id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                          <span className="text-2xl">{request.avatar}</span>
                        </div>

                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {request.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {request.username}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {t('friends.requestSent')}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleCancelSentRequest(request.userId)}
                          className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          {t('friends.cancelRequest')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Followers */}
        {activeTab === 'followers' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('friends.yourFollowers')} ({filteredData.length})
              </h2>
            </div>

            {filteredData.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                <UserCheck className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>{t('friends.noFollowersYet')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredData.map((follower) => (
                  <div
                    key={follower.id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                          <span className="text-2xl">{follower.avatar}</span>
                        </div>

                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {follower.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {follower.username}
                            {follower.isFollowingBack && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                {t('friends.followingBack')}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {!follower.isFollowingBack ? (
                          <button
                            onClick={() => handleFollowBack(follower.id)}
                            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <UserPlus className="w-4 h-4" />
                            {t('friends.followBack')}
                          </button>
                        ) : (
                          <button
                            onClick={() => onStartMessage && onStartMessage(follower.id)}
                            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <MessageCircle className="w-4 h-4" />
                            {t('friends.message')}
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveFollower(follower.id)}
                          className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <UserX className="w-4 h-4" />
                          {t('friends.remove')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Following */}
        {activeTab === 'following' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('friends.following')} ({filteredData.length})
              </h2>
            </div>

            {filteredData.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                <UserMinus className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>{t('friends.notFollowingYet')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredData.map((person) => (
                  <div
                    key={person.id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                          <span className="text-2xl">{person.avatar}</span>
                        </div>

                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {person.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {person.username}
                            {person.isFollower && (
                              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                {t('friends.followsYou')}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => onStartMessage && onStartMessage(person.id)}
                          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <MessageCircle className="w-4 h-4" />
                          {t('friends.message')}
                        </button>
                        <button
                          onClick={() => handleUnfollow(person.id)}
                          className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <UserMinus className="w-4 h-4" />
                          {t('friends.unfollow')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
